import { useState, useMemo } from 'react';
import { Input } from '~/components/ui/input';
import { Checkbox } from '~/components/ui/checkbox';
import { Button } from '~/components/ui/button';
import { Combobox } from '~/components/ui/combobox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

interface Student {
  id: string;
  firstName: string | null;
  lastName: string | null;
  dateOfBirth?: string | null;
  familyId: string;
  familyName: string;
}

interface StudentSelection {
  personId: string;
  selected: boolean;
  gradeLevel: string;
  enrollmentKind: string | null;
}

interface GradeLevel {
  description: string;
  order: number;
}

interface SelectStudentsStepProps {
  students: Student[];
  selections: StudentSelection[];
  onSelectionsChange: (selections: StudentSelection[]) => void;
  gradeLevels: GradeLevel[];
  enrollmentKind: string;
}

function SelectStudentsStep({
  students,
  selections,
  onSelectionsChange,
  gradeLevels,
  enrollmentKind
}: SelectStudentsStepProps) {
  const [search, setSearch] = useState('');
  const [customGrades, setCustomGrades] = useState<string[]>([]);

  // Combine configured grade levels with any custom ones added
  const allGradeLevels = useMemo(() => {
    const configured = gradeLevels.map(gl => ({
      value: gl.description,
      label: gl.description,
    }));
    const custom = customGrades.map(g => ({
      value: g,
      label: g,
    }));
    return [...configured, ...custom];
  }, [gradeLevels, customGrades]);

  const filteredStudents = useMemo(() => {
    if (!search) return students;
    const searchLower = search.toLowerCase();
    return students.filter(
      s =>
        (s.firstName ?? '').toLowerCase().includes(searchLower) ||
        (s.lastName ?? '').toLowerCase().includes(searchLower) ||
        s.familyName.toLowerCase().includes(searchLower)
    );
  }, [students, search]);

  const handleToggleAll = (checked: boolean) => {
    onSelectionsChange(
      selections.map(s => ({ ...s, selected: checked }))
    );
  };

  const handleToggleStudent = (personId: string) => {
    onSelectionsChange(
      selections.map(s =>
        s.personId === personId ? { ...s, selected: !s.selected } : s
      )
    );
  };

  const handleGradeChange = (personId: string, gradeLevel: string) => {
    onSelectionsChange(
      selections.map(s =>
        s.personId === personId ? { ...s, gradeLevel } : s
      )
    );
  };

  const handleEnrollmentKindChange = (personId: string, kind: string) => {
    onSelectionsChange(
      selections.map(s =>
        s.personId === personId ? { ...s, enrollmentKind: kind } : s
      )
    );
  };

  const handleAddNewGrade = (newGrade: string) => {
    if (!customGrades.includes(newGrade)) {
      setCustomGrades([...customGrades, newGrade]);
    }
  };

  const getStudentSelection = (personId: string): StudentSelection => {
    return selections.find(s => s.personId === personId) || { personId, selected: false, gradeLevel: '', enrollmentKind: null };
  };

  const selectedCount = selections.filter(s => s.selected).length;
  const allSelected = filteredStudents.length > 0 &&
    filteredStudents.every(s => getStudentSelection(s.id).selected);
  const someSelected = filteredStudents.some(s => getStudentSelection(s.id).selected);

  // Check if this is a returning family with potentially new siblings
  const isReEnrollment = enrollmentKind === 'RE_ENROLLMENT';

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Select the students to enroll and optionally assign their grade levels.
        {isReEnrollment && (
          <span className="block mt-1">
            For returning families with new siblings, you can mark individual students as &quot;New Student&quot;.
          </span>
        )}
      </p>

      <div className="flex items-center gap-4">
        <Input
          placeholder="Search students..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <div className="text-sm text-muted-foreground">
          {selectedCount} of {students.length} selected
        </div>
      </div>

      {students.length === 0 ? (
        <div className="rounded-md border p-8 text-center text-muted-foreground">
          No students available to enroll. All children from the selected families
          may already have enrollments for this school year.
        </div>
      ) : (
        <>
          <div className="rounded-md border max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={allSelected || (someSelected && 'indeterminate')}
                      onCheckedChange={handleToggleAll}
                      aria-label="Select all"
                    />
                  </TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Family</TableHead>
                  <TableHead>Grade Level</TableHead>
                  {isReEnrollment && <TableHead>Type</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredStudents.map(student => {
                  const selection = getStudentSelection(student.id);
                  return (
                    <TableRow key={student.id}>
                      <TableCell>
                        <Checkbox
                          checked={selection.selected}
                          onCheckedChange={() => handleToggleStudent(student.id)}
                          aria-label={`Select ${student.firstName} ${student.lastName}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {student.firstName} {student.lastName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {student.familyName}
                      </TableCell>
                      <TableCell className="w-48">
                        <Combobox
                          options={allGradeLevels}
                          value={selection.gradeLevel}
                          onValueChange={(value) => handleGradeChange(student.id, value)}
                          placeholder="Select grade..."
                          searchPlaceholder="Search or add..."
                          emptyMessage="No grades found"
                          allowCreate={true}
                          onCreateNew={handleAddNewGrade}
                          disabled={!selection.selected}
                        />
                      </TableCell>
                      {isReEnrollment && (
                        <TableCell className="w-40">
                          <Select
                            value={selection.enrollmentKind || 'RETURNING_STUDENT'}
                            onValueChange={(value) => handleEnrollmentKindChange(student.id, value)}
                            disabled={!selection.selected}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Returning" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="RETURNING_STUDENT">Returning</SelectItem>
                              <SelectItem value="NEW_STUDENT">New Student</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleToggleAll(true)}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleToggleAll(false)}
            >
              Clear Selection
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default SelectStudentsStep;
